import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay, faCheck, faChevronDown, faCircleCheck, faClock, faPlay, faRotateLeft, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import AppShell, { PageHeader } from "../components/shell/app-shell";
import Icon3D from "../components/ui/icon-3d";
import SearchInput from "../components/ui/search-input";
import Select from "../components/ui/select";
import SidePanel from "../components/ui/side-panel";
import StatusPill from "../components/ui/status-pill";
import PulseDot from "../components/ui/pulse-dot";
import StatTile from "../components/ui/stat-tile";
import EmptyState from "../components/ui/empty-state";
import SkeletonList from "../components/ui/skeleton-list";
import { Fields, PanelTitle } from "../components/ui/detail";
import { TONES } from "../components/ui/tones";
import TimeAgo from "../components/time-ago";
import useQueryState, { useDebouncedQueryState } from "../hooks/use-query-state";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { platformParam, usePlatform } from "../store/use-platform";
import PolicyChip from "../components/logs/policy-chip";
import { fetchCronJob, fetchCronJobs, fetchJobRuns, fetchRunFailures, updateAlert } from "../utils/api";
import { BAD_RUN, PLATFORM_LOOK, RUN_LOOK, describeSchedule, platformLabel } from "../utils/status";
import { formatDateTime, formatDuration } from "../utils/format-time";
import { CronJob, CronJobDetail, JobRun, RunFailure, RunTick } from "../types/job-run";
import { ParseErrors } from "../utils/ParseErrors";

const STATUS_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Needs attention", value: "failed,crashed,missed,stuck", dot: "bg-rose-500" },
  { label: "Running", value: "running", dot: "bg-sky-500" },
  { label: "Partial", value: "partial", dot: "bg-amber-500" },
  { label: "Success", value: "success", dot: "bg-emerald-500" },
  { label: "Skipped", value: "skipped", dot: "bg-slate-400" },
];

// ----------------------- RUN STRIP ------------------------------------
function RunStrip({ runs, slots = 24 }: { runs: RunTick[]; slots?: number }) {
  const shown = runs.slice(-slots);
  return (
    <div className="flex h-5 items-end gap-[3px]" aria-label="Recent runs">
      {Array.from({ length: Math.max(0, slots - shown.length) }).map((_, index) => (
        <span key={`empty-${index}`} className="h-full flex-1 rounded-[3px] bg-overlay-mid" />
      ))}
      {shown.map((run) => (
        <span
          key={run.id}
          title={`${RUN_LOOK[run.status].label} · ${formatDateTime(run.startedAt)}`}
          className={clsx("h-full flex-1 rounded-[3px] transition-transform duration-300 hover:scale-y-125", TONES[RUN_LOOK[run.status].tone].dot, run.status === "missed" && "border border-dashed border-rose-500 !bg-transparent")}
        />
      ))}
    </div>
  );
}

// ----------------------- ONE RUN ROW WITH ITS FAILED ITEMS ------------------------------------
function RunRow({ run }: { run: JobRun }) {
  const [open, setOpen] = useState(false);
  const [failures, setFailures] = useState<RunFailure[] | null>(null);
  const look = RUN_LOOK[run.status];

  const toggle = async () => {
    setOpen(!open);
    if (failures || !run.failed) return;
    try {
      setFailures(await fetchRunFailures(run.id));
    } catch (error) {
      toast.error(ParseErrors(error));
    }
  };

  return (
    <li className="glass overflow-hidden p-0">
      <button type="button" onClick={toggle} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-overlay">
        <PulseDot tone={look.tone} live={look.live} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-[Gilroy-Medium] text-[13px] text-heading-color">{run.summary ?? look.label}</span>
          <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">{formatDateTime(run.startedAt)} · {formatDuration(run.durationMs)}</span>
        </span>
        {run.total != null ? <span className="font-mono text-[12px] text-light-gray">{run.total - (run.failed ?? 0)}/{run.total}</span> : null}
        <FontAwesomeIcon icon={faChevronDown} className={clsx("text-[10px] text-label-muted transition-transform duration-300", open && "rotate-180")} />
      </button>
      <div className={clsx("grid transition-all duration-300 ease-out", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
        <div className="overflow-hidden">
          <div className="space-y-2 border-t border-edge px-4 py-3 text-[12px]">
            <p className="font-[Gilroy-Medium] text-light-gray">
              {look.label} · {run.total ?? 0} items · {run.failed ?? 0} failed · {run.skipped ?? 0} skipped · reported by {run.source === "checker" ? "HealthSure" : `SDK ${run.source}`}
            </p>
            {failures?.map((failure) => (
              <div key={failure.id} className="rounded-xl border border-rose-500/25 bg-rose-500/5 px-3 py-2">
                <p className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-heading-color">{failure.ref ? <PolicyChip value={failure.ref} /> : "—"}{failure.step ? <span>{failure.step.replace(/_/g, " ")}</span> : null}</p>
                <p className="mt-0.5 break-words font-[Gilroy-Medium] text-rose-600 dark:text-rose-300">{failure.error}</p>
              </div>
            ))}
            {run.error ? <pre className="custom-scrollbar overflow-auto rounded-xl bg-[#0b1020] p-3 font-mono text-[11px] text-indigo-100">{JSON.stringify(run.error, null, 2)}</pre> : null}
          </div>
        </div>
      </div>
    </li>
  );
}

// ----------------------- A FAILED LAST RUN THAT NOBODY RESOLVED OR MUTED ------------------------------------
const isResolved = (job?: CronJob) => !!job?.alertState && ["resolved", "muted"].includes(job.alertState);
const needsAttention = (job: CronJob) => !!job.lastStatus && BAD_RUN.includes(job.lastStatus) && !isResolved(job);

// ----------------------- JOB SIDE PANEL ------------------------------------
function JobPanel({ jobId, listed, onClose, onChanged }: { jobId: number | null; listed?: CronJob; onClose: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<CronJobDetail | null>(null);
  const [runs, setRuns] = useState<JobRun[]>([]);

  useEffect(() => {
    setDetail(null);
    setRuns([]);
    if (!jobId) return;
    Promise.all([fetchCronJob(jobId), fetchJobRuns(jobId, { limit: 30 })])
      .then(([job, list]) => {
        setDetail(job);
        setRuns(list.data);
      })
      .catch((error) => toast.error(ParseErrors(error)));
  }, [jobId]);

  const job = detail?.job;
  const look = job?.lastStatus ? RUN_LOOK[job.lastStatus] : null;
  const canResolve = !!listed?.lastRunId && (needsAttention(listed) || isResolved(listed));

  const setResolved = async (action: "resolve" | "reopen") => {
    if (!listed) return;
    setBusy(true);
    try {
      await updateAlert(`cron:${listed.id}:${listed.lastRunId}`, action);
      toast.success(action === "resolve" ? "Resolved · it turns red again only if a newer run fails" : "Reopened");
      onChanged();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setBusy(false);
    }
  };
  const chart = [...runs].reverse().map((run) => ({ id: run.id, seconds: Math.round((run.durationMs ?? 0) / 100) / 10, status: run.status, at: formatDateTime(run.startedAt) }));

  return (
    <SidePanel
      open={!!jobId}
      onClose={onClose}
      icon={job ? <Icon3D icon={faClock} tone={look?.tone ?? "violet"} size="lg" /> : null}
      title={job?.name ?? "Loading…"}
      subtitle={job ? `${platformLabel(job.platform)} · ${describeSchedule(job.schedule)}${job.timezone ? ` (${job.timezone})` : ""}` : undefined}
      badge={
        look ? (
          <>
            <StatusPill tone={isResolved(listed) ? "slate" : look.tone} live={look.live && !isResolved(listed)}>Last run: {look.label}</StatusPill>
            {isResolved(listed) ? <StatusPill tone="emerald">{listed?.alertState === "muted" ? "Muted" : "Resolved"}</StatusPill> : null}
          </>
        ) : null
      }
      footer={
        canResolve ? (
          <button type="button" disabled={busy} onClick={() => setResolved(isResolved(listed) ? "reopen" : "resolve")} className="inline-flex h-10 items-center gap-2 rounded-xl border border-edge bg-surface px-3.5 font-[Gilroy-Medium] text-[13px] text-light-gray transition-colors hover:border-accent hover:text-heading-color disabled:opacity-60">
            <FontAwesomeIcon icon={isResolved(listed) ? faRotateLeft : faCheck} className="text-[11px]" />
            {isResolved(listed) ? "Reopen" : "Mark resolved"}
          </button>
        ) : null
      }
    >
      {!detail ? (
        <SkeletonList rows={4} height="h-20" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Success rate", detail.stats.successRate != null ? `${detail.stats.successRate}%` : "—", `last ${detail.stats.days} days`],
              ["Runs", String(detail.stats.runs), `${detail.stats.missed} missed · ${detail.stats.stuck} stuck`],
              ["Avg duration", formatDuration(detail.stats.avgDurationMs), `max ${formatDuration(detail.stats.maxDurationMs)}`],
              ["Items failed", String(detail.stats.itemsFailed), detail.stats.topError ? `top: ${detail.stats.topError}` : "none"],
            ].map(([label, value, note]) => (
              <div key={label} className="glass p-3.5">
                <p className="font-[Gilroy-Medium] text-[12px] text-label-muted">{label}</p>
                <p className="font-[Gilroy-Bold] text-[22px] tabular-nums text-heading-color">{value}</p>
                <p className="truncate font-[Gilroy-Medium] text-[11px] text-light-gray">{note}</p>
              </div>
            ))}
          </div>

          {chart.length ? (
            <div className="glass p-4">
              <PanelTitle>Run duration (seconds)</PanelTitle>
              <div className="mt-3 h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart}>
                    <XAxis dataKey="at" hide />
                    <Tooltip cursor={{ fill: "rgba(99,102,241,0.08)" }} contentStyle={{ borderRadius: 12, border: "none", fontSize: 12 }} formatter={(value: number) => [`${value}s`, "Duration"]} />
                    <Bar dataKey="seconds" radius={[6, 6, 2, 2]} animationDuration={700}>
                      {chart.map((row) => (
                        <Cell key={row.id} fill={TONES[RUN_LOOK[row.status].tone].to} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <PanelTitle>Schedule</PanelTitle>
          <Fields
            rows={[
              ["Runs", describeSchedule(job!.schedule)],
              ["Cron", job!.schedule ? <span className="font-mono text-[12px]">{job!.schedule}</span> : null],
              ["Next expected", job!.nextExpectedAt ? <TimeAgo value={job!.nextExpectedAt} /> : null],
              ["Missed after", job!.graceMinutes ? `${job!.graceMinutes} min late` : "Not checked"],
              ["Stuck after", job!.maxDurationMinutes ? `${job!.maxDurationMinutes} min running` : "Not checked"],
              ["Production only", job!.productionOnly ? "Yes" : "No"],
            ]}
          />

          <PanelTitle>Recent runs</PanelTitle>
          {runs.length ? <ul className="space-y-2">{runs.map((run) => <RunRow key={run.id} run={run} />)}</ul> : <p className="text-[13px] text-light-gray">No runs reported yet.</p>}
        </>
      )}
    </SidePanel>
  );
}

// ----------------------- LAST RUN IN ONE LINE ------------------------------------
const lastRunLine = (job: CronJob) => {
  if (job.lastStatus === "running") return "Running now…";
  if (job.lastStatus === "missed") return "Didn't start on time";
  const run = job.lastRun;
  if (!run) return "No runs reported yet";
  if (run.summary) return run.summary;
  if (run.total != null) return `${run.total - (run.failed ?? 0)} of ${run.total} items done`;
  return `Finished in ${formatDuration(run.durationMs)}`;
};

export default function CronJobsPage() {
  const { platform } = usePlatform();
  const [search, searchInput, setSearchInput] = useDebouncedQueryState("search");
  const [status, setStatus] = useQueryState<string>("status", "");
  const [jobParam, setJobParam] = useQueryState<string>("job", "");
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setJobs((await fetchCronJobs({ search, lastStatus: status, platform: platformParam(platform) })).data);
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  }, [search, status, platform]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);
  useAutoRefresh(load, 30000);

  const totals = useMemo(
    () => ({
      all: jobs.length,
      running: jobs.filter((job) => job.lastStatus === "running").length,
      bad: jobs.filter(needsAttention).length,
      ok: jobs.filter((job) => job.lastStatus === "success" || job.lastStatus === "skipped").length,
    }),
    [jobs],
  );

  return (
    <AppShell>
      <PageHeader title="Cron jobs" subtitle="Every scheduled job on every platform: is it running, did it work, and did it run on time." />

      <div className="stagger mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Jobs tracked" value={totals.all} icon={faCalendarDay} tone="violet" />
        <StatTile label="Running now" value={totals.running} icon={faPlay} tone="sky" />
        <StatTile label="Need attention" value={totals.bad} icon={faTriangleExclamation} tone="rose" note="failed, crashed, missed or stuck" />
        <StatTile label="Healthy" value={totals.ok} icon={faCircleCheck} tone="emerald" />
      </div>

      <div className="glass relative z-20 mb-5 flex flex-col gap-2 p-2.5 sm:flex-row">
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search jobs" />
        <Select label="Last run" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
      </div>

      {loading ? (
        <SkeletonList rows={3} height="h-44" />
      ) : !jobs.length ? (
        <div className="glass"><EmptyState icon={faClock} tone="violet" title="No cron jobs yet" text="Jobs appear once a platform registers them with SDK v2 or reports its first run." /></div>
      ) : (
        <div className="stagger grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {jobs.map((job) => {
            const look = job.lastStatus ? RUN_LOOK[job.lastStatus] : null;
            const bad = needsAttention(job);
            const resolved = isResolved(job) && !!job.lastStatus && BAD_RUN.includes(job.lastStatus);
            const platformLook = PLATFORM_LOOK[job.platform];
            return (
              <button
                key={job.id}
                type="button"
                onClick={() => setJobParam(String(job.id))}
                className={clsx("glass glass-hover group relative flex flex-col gap-4 overflow-hidden p-5 text-left", bad && "border-rose-500/50")}
              >
                <div className="flex items-start gap-3">
                  <Icon3D icon={faClock} tone={look?.tone ?? "slate"} size="md" flat />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-[Gilroy-Bold] text-[16px] text-heading-color">{job.name}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 font-[Gilroy-Medium] text-[12px] text-label-muted">
                      {platformLook ? <span className={clsx("h-2 w-2 rounded-full", TONES[platformLook.tone].dot)} /> : null}
                      {platformLabel(job.platform)} · {describeSchedule(job.schedule)}
                    </p>
                  </div>
                  {resolved ? <StatusPill tone="slate">Resolved</StatusPill> : look ? <StatusPill tone={look.tone} live={look.live}>{look.label}</StatusPill> : <StatusPill tone="slate">No runs</StatusPill>}
                </div>
                <p className="line-clamp-2 min-h-[40px] font-[Gilroy-Medium] text-[13px] text-heading-color">{lastRunLine(job)}</p>
                <RunStrip runs={job.recentRuns} />
                <div className="flex justify-between font-[Gilroy-Medium] text-[12px] text-label-muted">
                  <span>Last <TimeAgo value={job.lastRunAt} /></span>
                  {job.nextExpectedAt ? <span>Next <TimeAgo value={job.nextExpectedAt} /></span> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <JobPanel jobId={jobParam ? Number(jobParam) : null} listed={jobs.find((job) => String(job.id) === jobParam)} onClose={() => setJobParam("")} onChanged={load} />
    </AppShell>
  );
}
